import { Bell } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  NotificationListItem,
  NotificationListStates,
} from '@/components/notifications/notification-list-item';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useConsoleNotifications,
  useConsoleUnreadCount,
  useHasConsoleNotificationAccess,
  useMarkConsoleNotificationsRead,
} from '@/hooks/use-console-notifications';
import { cn } from '@/utils/cn';

const PREVIEW_PAGE_SIZE = 8;

export const NotificationCenter = () => {
  const { t } = useTranslation('notifications');
  const hasAccess = useHasConsoleNotificationAccess();
  const [open, setOpen] = useState(false);

  const unreadQuery = useConsoleUnreadCount();
  const listQuery = useConsoleNotifications({
    page: 1,
    pageSize: PREVIEW_PAGE_SIZE,
  });
  const markRead = useMarkConsoleNotificationsRead();

  if (!hasAccess) return null;

  const unreadCount = unreadQuery.data ?? listQuery.data?.totals.unreadCount ?? 0;
  const badgeLabel = unreadCount > 99 ? '99+' : unreadCount > 0 ? String(unreadCount) : undefined;

  const notifications = listQuery.data?.data ?? [];
  const showMarkAll = unreadCount > 0 && !markRead.isPending;

  const handleOpen = (notification: { id: string; readAt: string | null }) => {
    if (notification.readAt != null) return;
    markRead.mutate({ notificationIds: [notification.id] });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative shrink-0"
          aria-label={unreadCount > 0 ? t('unreadCount', { count: unreadCount }) : t('open')}
        >
          <Bell className="size-5" aria-hidden />
          {badgeLabel ? (
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5 flex min-w-[1.125rem] items-center justify-center rounded-full',
                'bg-brand px-1 text-[10px] font-semibold leading-4 text-on-brand',
              )}
              aria-hidden
            >
              {badgeLabel}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        matchTriggerWidth={false}
        className="w-[min(100vw-2rem,24rem)] p-0"
        collisionPadding={{ top: 72, right: 16, bottom: 16, left: 16 }}
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
          <div>
            <p className="text-body-sm font-semibold text-fg">{t('title')}</p>
            <p className="text-caption text-fg-secondary">{t('unreadCount', { count: unreadCount })}</p>
          </div>
          {showMarkAll ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={() => markRead.mutate({ readAll: true })}
            >
              {t('markAllRead')}
            </Button>
          ) : null}
        </div>

        <ScrollArea className="max-h-[min(24rem,var(--radix-popover-content-available-height))]">
          <div className="p-1">
            <NotificationListStates
              loading={listQuery.isLoading}
              error={listQuery.isError}
              empty={!listQuery.isLoading && !listQuery.isError && notifications.length === 0}
              onRetry={() => {
                listQuery.refetch();
              }}
            />
            {notifications.map((notification) => (
              <NotificationListItem
                key={notification.id}
                notification={notification}
                compact
                onOpen={handleOpen}
              />
            ))}
          </div>
        </ScrollArea>

        <div className="flex justify-end border-t border-border px-3 py-2">
          <Button type="button" variant="ghost" size="sm" asChild>
            <Link to="/notifications" onClick={() => setOpen(false)}>
              {t('viewAll')}
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
